import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Progress } from '../../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ClipboardCheck, PenLine, Undo2, Users, Clock, CheckCircle2, AlertTriangle, Route, Loader2, ListChecks, Brain, Check, MessageSquareText, ChevronDown, ChevronUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const STATUS_LABEL = {
  no_path: { text: 'No path assigned', cls: 'bg-stone-100 text-stone-500' },
  complete: { text: 'Path complete', cls: 'bg-green-100 text-green-700' },
  pending_signoff: { text: 'Awaiting sign-off', cls: 'bg-amber-100 text-amber-700' },
  rejected: { text: 'Sent back', cls: 'bg-red-100 text-red-700' },
  in_progress: { text: 'In progress', cls: 'bg-orange-100 text-[#D9480F]' },
  available: { text: 'Not started', cls: 'bg-stone-100 text-stone-600' },
};

function AnswerReview({ review }) {
  const [open, setOpen] = useState(true);
  const sections = review?.sections || [];
  const total = sections.reduce((n, s) => n + s.review.length, 0);
  if (sections.length === 0) return <p className="text-xs text-stone-400 bg-stone-50 rounded-xl px-3 py-2" data-testid="answer-review-empty">No quiz answers recorded for review.</p>;
  return (
    <div className="rounded-xl border border-stone-200" data-testid="answer-review">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3 py-2.5 text-left">
        <span className="text-sm font-semibold text-stone-900 flex items-center gap-2"><MessageSquareText className="w-4 h-4 text-[#D9480F]" /> Their answers <span className="text-xs font-normal text-stone-400">{review.short_answers} short answer{review.short_answers !== 1 ? 's' : ''}{total - review.short_answers > 0 ? ` · ${total - review.short_answers} missed` : ''}</span></span>
        {open ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 max-h-72 overflow-y-auto">
          {sections.map((sec, si) => (
            <div key={si}>
              <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">{sec.title} · {sec.score}%</p>
              {sec.review.length === 0 && <p className="text-xs text-stone-500 mt-1">All auto-graded questions correct.</p>}
              {sec.review.map((r, ri) => (
                <div key={ri} className="mt-1.5 rounded-lg bg-stone-50 border border-stone-100 p-2.5" data-testid={`answer-review-item-${si}-${ri}`}>
                  <p className="text-xs font-medium text-stone-800">{r.question}</p>
                  {r.type === 'short_answer' ? (
                    <>
                      <p className="text-xs text-stone-700 mt-1 italic whitespace-pre-line">"{r.user_answer || '—'}"</p>
                      <div className="flex items-start gap-2 mt-1.5">
                        <Badge className={`text-[10px] shrink-0 ${r.ai_score >= 70 ? 'bg-green-100 text-green-700' : r.ai_score >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>AI {r.ai_score}%</Badge>
                        {r.feedback && <p className="text-[11px] text-stone-500">{r.feedback}</p>}
                      </div>
                    </>
                  ) : (
                    <p className="text-[11px] text-red-600 mt-1">Missed — chose: {Array.isArray(r.user_answer) ? r.user_answer.map(i => r.options?.[i] ?? i).join(', ') : (r.options?.[r.user_answer] ?? String(r.user_answer ?? '—'))}</p>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SignoffDialog({ item, mode, onClose, onDone }) {
  const { api, user } = useAuth();
  const [signature, setSignature] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState([]);
  const isApprove = mode === 'approve';
  const checklist = item?.practical_checklist || [];
  const allChecked = checklist.every(c => checked.includes(c.id));
  const toggleItem = (id) => setChecked(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const submit = async () => {
    setBusy(true);
    try {
      if (isApprove) await api('post', `/signoffs/${item.id}/approve`, { signature_name: signature, confirmed, checked_items: checked });
      else await api('post', `/signoffs/${item.id}/reject`, { note });
      toast.success(isApprove ? `Signed off "${item.module_title}"` : 'Module sent back to team member');
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto" data-testid={`${mode}-dialog`}>
        <DialogHeader><DialogTitle style={{ fontFamily: "'Outfit', sans-serif" }}>{isApprove ? 'Digital Sign-off' : 'Send Module Back'}</DialogTitle></DialogHeader>
        {item && (
          <div className="space-y-4">
            <div className="bg-stone-50 rounded-xl p-3 border border-stone-100 text-sm">
              <p className="font-semibold text-stone-900">{item.user_name}</p>
              <p className="text-stone-600">{item.module_title}</p>
              <p className="text-xs text-stone-400 mt-1">{item.path_name} · Quiz score {item.quiz_score}% · {new Date(item.completed_at).toLocaleString()}</p>
            </div>
            {isApprove && <AnswerReview review={item.answer_review} />}
            {isApprove ? (
              <>
                {checklist.length > 0 && (
                  <div className="rounded-xl border border-[#D9480F]/30 bg-[#FFF4E6] p-3" data-testid="practical-checklist">
                    <div className="flex items-center gap-2 mb-2"><ListChecks className="w-4 h-4 text-[#D9480F]" /><p className="text-sm font-semibold text-stone-900">Practical skills — physically verified</p></div>
                    <p className="text-[11px] text-stone-500 mb-2">Tick each skill only after watching {item.user_name.split(' ')[0]} demonstrate it.</p>
                    <div className="space-y-2">
                      {checklist.map(c => (
                        <label key={c.id} className="flex items-start gap-2.5 cursor-pointer">
                          <Checkbox checked={checked.includes(c.id)} onCheckedChange={() => toggleItem(c.id)} className="mt-0.5" data-testid={`practical-item-${c.id}`} />
                          <span className="text-sm text-stone-700">{c.text}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label>Type your full name to sign</Label>
                  <Input value={signature} onChange={e => setSignature(e.target.value)} placeholder={user?.name} className="rounded-xl mt-1 font-semibold italic" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="signature-input" />
                  <p className="text-[11px] text-stone-400 mt-1">Must match your name on file: {user?.name}</p>
                </div>
                <label className="flex items-start gap-3 p-3 rounded-xl border border-stone-200 cursor-pointer hover:bg-stone-50">
                  <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} className="mt-0.5" data-testid="signoff-confirm-checkbox" />
                  <span className="text-sm text-stone-700">I have reviewed this team member's work and confirm they have demonstrated the required knowledge for this module. My name, the date and time will be recorded as a digital signature.</span>
                </label>
                {!allChecked && <p className="text-xs text-amber-700 text-center">Verify all {checklist.length} practical skills to enable sign-off</p>}
                <Button onClick={submit} disabled={busy || !confirmed || !signature.trim() || !allChecked} className="w-full bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="confirm-signoff-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PenLine className="w-4 h-4 mr-2" /> Sign & Approve</>}
                </Button>
              </>
            ) : (
              <>
                <div>
                  <Label>Note to team member <span className="text-red-500">*</span></Label>
                  <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="What should they review before retaking the quiz?" className="rounded-xl mt-1" rows={3} data-testid="reject-note-input" />
                </div>
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">They will need to retake the quiz before this module returns to your queue.</p>
                <Button onClick={submit} disabled={busy || !note.trim()} className="w-full bg-red-600 text-white rounded-xl hover:bg-red-700" data-testid="confirm-reject-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Undo2 className="w-4 h-4 mr-2" /> Send Back</>}
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function TeamTab() {
  const { api } = useAuth();
  const [queue, setQueue] = useState([]);
  const [team, setTeam] = useState([]);
  const [flags, setFlags] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [q, t, f] = await Promise.all([api('get', '/signoffs?status=pending_signoff'), api('get', '/team/path-progress'), api('get', '/refreshers/flags')]);
      setQueue(q.data); setTeam(t.data); setFlags(f.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const ackFlag = async (id) => {
    try { await api('post', `/refreshers/${id}/acknowledge`); toast.success('Flag cleared'); load(); }
    catch { toast.error('Failed to clear flag'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1B2A4A]" /></div>;

  return (
    <div data-testid="team-tab">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <ClipboardCheck className="w-5 h-5 text-[#D9480F]" />
          <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Sign-off Queue</h2>
          <Badge className={queue.length ? 'bg-amber-100 text-amber-700' : 'bg-stone-100 text-stone-500'} data-testid="signoff-queue-count">{queue.length}</Badge>
        </div>
        {queue.length === 0 ? (
          <Card className="bg-white rounded-2xl border border-stone-200"><CardContent className="p-6 text-center"><CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" /><p className="text-sm text-stone-500">All caught up — no modules waiting for sign-off</p></CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="signoff-queue">
            {queue.map((item, i) => (
              <motion.div key={item.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Card className="bg-white rounded-2xl border border-amber-200 shadow-sm" data-testid={`signoff-item-${item.id}`}>
                  <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.user_name}</p>
                        {item.user_position && <Badge className="bg-violet-100 text-violet-700 text-[10px]">{item.user_position}</Badge>}
                        {item.user_location && <span className="text-xs text-stone-400">{item.user_location}</span>}
                      </div>
                      <p className="text-sm text-stone-700 mt-0.5">{item.module_title}</p>
                      <p className="text-xs text-stone-400 mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {item.path_name} · Quiz {item.quiz_score}% · {new Date(item.completed_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => setDialog({ item, mode: 'reject' })} className="rounded-xl border-stone-200 text-stone-600" data-testid={`reject-btn-${item.id}`}><Undo2 className="w-3.5 h-3.5 mr-1" /> Send Back</Button>
                      <Button size="sm" onClick={() => setDialog({ item, mode: 'approve' })} className="rounded-xl bg-[#1B2A4A] text-white hover:bg-[#243658]" data-testid={`approve-btn-${item.id}`}><PenLine className="w-3.5 h-3.5 mr-1" /> Sign Off</Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {flags.length > 0 && (
        <div className="mb-6" data-testid="refresher-flags">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-5 h-5 text-[#D9480F]" />
            <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Refresher Flags</h2>
            <Badge className="bg-red-100 text-red-700" data-testid="refresher-flags-count">{flags.length}</Badge>
          </div>
          <div className="space-y-2">
            {flags.map(f => (
              <Card key={f.id} className="bg-white rounded-2xl border border-red-200 shadow-sm" data-testid={`refresher-flag-${f.id}`}>
                <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.user_name}</p>
                    <p className="text-sm text-stone-700">{f.module_title}</p>
                    <p className="text-xs text-stone-400 mt-0.5">{f.interval_days}-day refresher · scored {f.score}% · {f.taken_at ? new Date(f.taken_at).toLocaleDateString() : ''}</p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => ackFlag(f.id)} className="rounded-xl border-stone-200" data-testid={`ack-flag-${f.id}`}><Check className="w-3.5 h-3.5 mr-1" /> Re-coached</Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <Users className="w-5 h-5 text-[#D9480F]" />
        <h2 className="text-lg font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Team Progress</h2>
      </div>
      {team.length === 0 ? (
        <Card className="bg-white rounded-2xl border border-stone-200"><CardContent className="p-8 text-center"><Users className="w-12 h-12 text-stone-300 mx-auto mb-3" /><p className="text-stone-500">No team members yet</p></CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2" data-testid="team-progress-list">
          {team.map((m) => {
            const st = STATUS_LABEL[m.status] || STATUS_LABEL.available;
            return (
              <Card key={m.user_id} className="bg-white rounded-2xl border border-stone-200 shadow-sm" data-testid={`team-member-${m.user_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-stone-900 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.name}</h3>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap text-xs text-stone-400">
                        <Badge className="bg-stone-100 text-stone-600 text-[10px]">{m.role}</Badge>
                        {m.position && <Badge className="bg-violet-100 text-violet-700 text-[10px]">{m.position}</Badge>}
                        {m.location && <span>{m.location}</span>}
                      </div>
                    </div>
                    <Badge className={`${st.cls} text-[10px] shrink-0`} data-testid={`team-member-status-${m.user_id}`}>{st.text}</Badge>
                  </div>
                  <div className="mt-3 bg-stone-50 rounded-xl p-3 border border-stone-100">
                    {m.current_module ? (
                      <>
                        <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold flex items-center gap-1"><Route className="w-3 h-3" /> {m.current_path_name} · Module {m.current_module.order + 1}/{m.current_module.path_total}</p>
                        <p className="text-sm font-medium text-stone-800 mt-0.5 truncate" data-testid={`team-member-current-${m.user_id}`}>{m.current_module.title}</p>
                      </>
                    ) : (
                      <p className="text-sm text-stone-500">{m.status === 'complete' ? 'All path modules signed off' : 'No modules in path yet'}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-3">
                    <Progress value={m.progress_percentage} className="h-2 flex-1 bg-stone-100" />
                    <span className="text-xs font-medium text-stone-600 shrink-0">{m.approved_modules}/{m.total_modules} signed off</span>
                  </div>
                  {m.pending_signoffs > 0 && (
                    <p className="text-xs text-amber-700 mt-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {m.pending_signoffs} module{m.pending_signoffs > 1 ? 's' : ''} awaiting your sign-off</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {dialog && <SignoffDialog item={dialog.item} mode={dialog.mode} onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />}
    </div>
  );
}
